<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';

import { useTranslations } from '@/shared/composables/useTranslations';

const { t } = useTranslations();

import IconMinus from './Icons/IconMinus.vue';
import IconPlus from './Icons/IconPlus.vue';

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const props = withDefaults(
  defineProps<{
    modelValue: number;
    min?: number;
    max?: number;
    step?: number;
    inputLabel?: string;
    decreaseLabel?: string;
    increaseLabel?: string;
    productName?: string;
    size?: 'compact' | 'regular';
    id?: string;
    wrapperClass?: string;
    buttonClass?: string;
    inputClass?: string;
  }>(),
  {
    min: 1,
    max: 9999,
    step: 1,
    inputLabel: undefined,
    decreaseLabel: undefined,
    increaseLabel: undefined,
    productName: undefined,
    size: 'regular',
    id: undefined,
    wrapperClass: undefined,
    buttonClass: undefined,
    inputClass: undefined
  }
);

const ariaInputLabel = computed(
  () => props.inputLabel || t('product.quantity.aria_label_amount')
);
const ariaDecreaseLabel = computed(() => {
  if (props.decreaseLabel) return props.decreaseLabel;
  return props.productName
    ? t('product.quantity.aria_label_reduce_product', {
        product: props.productName
      })
    : t('product.quantity.aria_label_reduce');
});
const ariaIncreaseLabel = computed(() => {
  if (props.increaseLabel) return props.increaseLabel;
  return props.productName
    ? t('product.quantity.aria_label_increase_product', {
        product: props.productName
      })
    : t('product.quantity.aria_label_increase');
});

// Use internal model for input value and handling sanitization
const model = ref<number | string>(props.modelValue);
let committingViaEnter = false;

// Update internal model if parent updates its ref
watch(
  () => props.modelValue,
  (newVal) => {
    model.value = newVal;
  }
);

const decrease = () => {
  const current =
    typeof model.value === 'string' ? parseInt(model.value, 10) : model.value;
  model.value = isNaN(current) ? (props.min ?? 1) : current - props.step;
  commit(model.value);
};

const increase = () => {
  const current =
    typeof model.value === 'string' ? parseInt(model.value, 10) : model.value;
  model.value = isNaN(current)
    ? (props.min ?? 1) + props.step
    : current + props.step;
  commit(model.value);
};

const commit = (value: number | string) => {
  const parsedVal = typeof value === 'string' ? parseInt(value, 10) : value;
  const min = props.min ?? 1;

  if (isNaN(parsedVal) || parsedVal < min) {
    emit('update:modelValue', min);
    model.value = min;
  } else {
    sanitize(value);
  }
};

// Attach to native `onkeydown` attribute, otherwise it's not possible to ignore keys
const keydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    committingViaEnter = true;
    commit(model.value);
    if (event.target instanceof HTMLInputElement) {
      event.target.blur();
    }
    committingViaEnter = false;
    event.preventDefault();
    return;
  }

  const allowedKeys = [
    'Tab',
    'Backspace',
    'Enter',
    'Delete',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End'
  ];

  if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
    return;
  }

  if (isNaN(parseInt(event.key))) {
    event.preventDefault();
  }
};

const blur = (e: FocusEvent) => {
  if (committingViaEnter) return;
  if (!(e.currentTarget instanceof HTMLInputElement)) return;
  commit(e.currentTarget.value);
};

const sanitize = (val: number | string) => {
  let parsedVal = typeof val === 'string' ? parseInt(val, 10) : val;

  if (isNaN(parsedVal)) return;

  if (typeof props.max !== 'undefined' && parsedVal > props.max) {
    parsedVal = props.max;
  }
  if (typeof props.min !== 'undefined' && parsedVal < props.min) {
    parsedVal = props.min;
  }

  model.value = parsedVal;
  emit('update:modelValue', parsedVal);
};
</script>

<template>
  <div
    class="inline-flex overflow-hidden rounded-lg border px-0.5"
    :class="wrapperClass"
  >
    <div :class="size === 'compact' ? 'flex items-center p-0' : 'p-1'">
      <button
        type="button"
        class="flex items-center justify-center border-0 bg-white text-center"
        :class="[
          size === 'compact'
            ? 'size-8 rounded-full text-base hover:text-black active:bg-gray-100'
            : 'size-12 rounded-md text-xl text-black outline-2 outline-primary/30 active:outline active:outline-primary/90',
          buttonClass
        ]"
        :aria-label="ariaDecreaseLabel"
        :title="decreaseLabel"
        :disabled="model === min"
        @click="decrease"
      >
        <IconMinus class="text-base" />
      </button>
    </div>
    <input
      :id="id || useId()"
      v-model="model"
      type="number"
      inputmode="numeric"
      autocomplete="off"
      class="m-0 appearance-none border-0 text-center font-semibold text-gray-darkest shadow-none outline-none focus:shadow-none focus:outline-0 focus:ring-0"
      :class="[
        size === 'compact' ? 'w-6 px-0 py-2 text-sm' : 'w-12 p-0 text-lg',
        inputClass
      ]"
      :min="min"
      :max="max"
      :step="step"
      pattern="\d*"
      :aria-label="ariaInputLabel"
      aria-live="polite"
      @keydown="keydown"
      @blur="blur"
    />
    <div :class="size === 'compact' ? 'flex items-center p-0' : 'p-1'">
      <button
        type="button"
        class="flex items-center justify-center border-0 bg-white text-center"
        :class="[
          size === 'compact'
            ? 'size-8 rounded-full text-base hover:text-black active:bg-gray-100'
            : 'size-12 rounded-md text-xl text-black outline-2 outline-primary/30 active:outline active:outline-primary/90',
          buttonClass
        ]"
        :aria-label="ariaIncreaseLabel"
        :title="increaseLabel"
        :disabled="model === max"
        @click="increase"
      >
        <IconPlus />
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Remove UI arrows from number input */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
